import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import type Keycloak from 'keycloak-js';

import { clientDebugLog } from './clientDebugLog';
import { getKeycloak } from './keycloak';
import { parseKeycloakOAuthError } from './keycloakOAuthError';

import {
	clearPersistedKeycloakSession,
	decodeJwtPayload,
	getRefreshTokenDebugMeta,
	isAccessTokenUsable,
	type PersistedKeycloakSession,
	savePersistedKeycloakSession,
} from './keycloakTokenStorage';

/**
 * Minimum access-token lifetime (seconds) before a proactive refresh is attempted.
 * Scheduled refresh fires at `exp - KEYCLOAK_MIN_TOKEN_VALIDITY_SECONDS`.
 */
export const KEYCLOAK_MIN_TOKEN_VALIDITY_SECONDS = 60;

export type KeycloakTokenRefreshCallbacks = {
	onTokenUpdated: (token: string) => void;
	onSessionExpired?: () => void;
};

let refreshTimer: ReturnType<typeof setTimeout> | undefined;
/** JWT `exp` (seconds) the current proactive timer is armed for — used to skip redundant reschedules. */
let scheduledForExp: number | undefined;
let sessionExpiredHandled = false;
let logoutInProgress = false;
let tokenRefreshCallbacks: KeycloakTokenRefreshCallbacks | undefined;
let refreshInFlight: Promise<string | null> | null = null;
let visibilityListenerAttached = false;
let nativeResumeListenerAttached = false;
let removeNativeResumeListener: (() => void) | undefined;

/** Stops token refresh while hub logout clears local auth state. */
export async function beginKeycloakLogout(): Promise<void> {
	logoutInProgress = true;
	stopKeycloakTokenRefresh();

	// Drain any in-flight refresh so its onTokenUpdated cannot re-hydrate hub auth
	// after clearAccessToken (that race sends login → launcher).
	if (refreshInFlight) {
		await refreshInFlight.catch(() => null);
	}
}

/** Re-enables token refresh after hub logout completes. */
export function endKeycloakLogout(): void {
	logoutInProgress = false;
}

/** True while hub logout is tearing down Keycloak/local auth. */
export function isKeycloakLogoutInProgress(): boolean {
	return logoutInProgress;
}

/** True while a Keycloak token refresh network call is in progress. */
export function isKeycloakTokenRefreshInFlight(): boolean {
	return refreshInFlight !== null;
}

/**
 * Resolves when any in-flight token refresh finishes (or immediately if none).
 * Axios interceptors should await this (or {@link ensureFreshKeycloakToken}) before sending.
 */
export function waitForKeycloakTokenRefresh(): Promise<string | null> {
	if (refreshInFlight) {
		return refreshInFlight;
	}

	return Promise.resolve(getKeycloak().token ?? null);
}

type KeycloakRefreshErrorBody = {
	error?: string;
	error_description?: string;
};

function getKeycloakRefreshErrorResponse(error: unknown): Response | undefined {
	if (typeof error !== 'object' || error === null || !('response' in error)) {
		return undefined;
	}

	const response = (error as { response?: Response }).response;

	return response instanceof Response ? response : undefined;
}

/** Returns whether a Keycloak token refresh error includes an HTTP 400 response. */
export function isKeycloakRefreshHttp400Error(error: unknown): boolean {
	const response = getKeycloakRefreshErrorResponse(error);

	return response?.status === 400;
}

/** OAuth token-endpoint errors that mean the persisted session cannot be refreshed. */
const KEYCLOAK_FATAL_REFRESH_ERRORS = new Set(['invalid_grant', 'not_allowed']);

/**
 * Returns whether a Keycloak token refresh error means the session is no longer valid.
 * Matches OAuth `invalid_grant` (e.g. "Session not active") and `not_allowed`
 * (e.g. offline tokens rejected for the user or client).
 */
export async function isKeycloakRefreshFatalError(error: unknown): Promise<boolean> {
	const response = getKeycloakRefreshErrorResponse(error);
	if (!response || response.status !== 400) {
		return false;
	}

	try {
		const body = (await response.clone().json()) as KeycloakRefreshErrorBody;

		return typeof body.error === 'string' && KEYCLOAK_FATAL_REFRESH_ERRORS.has(body.error);
	} catch {
		return false;
	}
}

/** Writes the current Keycloak tokens to persistent storage when both are present. */
export function persistKeycloakTokens(keycloak: Keycloak): void {
	if (!keycloak.token || !keycloak.refreshToken) {
		return;
	}

	savePersistedKeycloakSession({
		token: keycloak.token,
		refreshToken: keycloak.refreshToken,
		idToken: keycloak.idToken,
		savedAt: Date.now(),
	});
}

/** Applies a persisted session to a Keycloak instance and marks it authenticated. */
export function applyPersistedTokensToKeycloak(
	keycloak: Keycloak,
	session: PersistedKeycloakSession
): void {
	keycloak.token = session.token;
	keycloak.refreshToken = session.refreshToken;
	keycloak.idToken = session.idToken;
	keycloak.tokenParsed = decodeJwtPayloadRecord(session.token);
	keycloak.refreshTokenParsed = decodeJwtPayloadRecord(session.refreshToken);
	keycloak.idTokenParsed = session.idToken ? decodeJwtPayloadRecord(session.idToken) : undefined;
	keycloak.authenticated = true;
	keycloak.subject = typeof keycloak.tokenParsed?.sub === 'string' ? keycloak.tokenParsed.sub : undefined;
	keycloak.sessionId =
		typeof keycloak.tokenParsed?.sid === 'string' ? keycloak.tokenParsed.sid : keycloak.sessionId;
}

/**
 * Clears persisted tokens, stops background refresh, and notifies the host app once.
 * Safe to call multiple times; subsequent calls are no-ops.
 */
export function handleKeycloakSessionExpired(): void {
	if (sessionExpiredHandled) {
		return;
	}

	sessionExpiredHandled = true;
	clientDebugLog('warn', '[auth:refresh] session expired handled', {
		hadCallback: Boolean(tokenRefreshCallbacks?.onSessionExpired),
		...getRefreshTokenDebugMeta(getKeycloak().refreshToken),
	});
	stopKeycloakTokenRefresh();
	clearPersistedKeycloakSession();
	tokenRefreshCallbacks?.onSessionExpired?.();
}

function clearScheduledRefresh(): void {
	if (refreshTimer !== undefined) {
		clearTimeout(refreshTimer);
		refreshTimer = undefined;
	}

	scheduledForExp = undefined;
}

/**
 * Schedules the next proactive refresh for `exp - minValidity` (default 1 minute before expiry).
 * No-ops when a timer is already armed for the same token `exp` (avoids re-scheduling on every API call).
 */
export function scheduleKeycloakTokenRefresh(
	token: string | undefined = getKeycloak().token,
	minValiditySeconds = KEYCLOAK_MIN_TOKEN_VALIDITY_SECONDS,
): void {
	if (typeof window === 'undefined' || sessionExpiredHandled || logoutInProgress || !token) {
		return;
	}

	const exp = decodeJwtPayload(token)?.exp;
	if (typeof exp !== 'number') {
		return;
	}

	if (refreshTimer !== undefined && scheduledForExp === exp) {
		return;
	}

	const delayMs = Math.max(0, (exp - minValiditySeconds) * 1000 - Date.now());

	clearScheduledRefresh();
	scheduledForExp = exp;

	refreshTimer = setTimeout(() => {
		refreshTimer = undefined;
		scheduledForExp = undefined;

		void (async () => {
			if (sessionExpiredHandled || logoutInProgress) {
				return;
			}

			const nextToken = await ensureFreshKeycloakToken(minValiditySeconds);
			if (nextToken) {
				// ensureFresh reschedules when needed
				return;
			}

			if (!getKeycloak().authenticated) {
				handleKeycloakSessionExpired();
			}
		})();
	}, delayMs);
}

async function runKeycloakTokenRefresh(minValidity: number): Promise<string | null> {
	if (sessionExpiredHandled || logoutInProgress) {
		return null;
	}

	const keycloak = getKeycloak();

	if (!keycloak.refreshToken) {
		if (keycloak.token) {
			scheduleKeycloakTokenRefresh(keycloak.token, minValidity);
		}

		return keycloak.token ?? null;
	}

	try {
		const refreshed = await keycloak.updateToken(minValidity);

		// Logout may have started while updateToken was in flight — discard the result.
		if (sessionExpiredHandled || logoutInProgress) {
			return null;
		}

		if ((refreshed || keycloak.token) && keycloak.token) {
			persistKeycloakTokens(keycloak);

			if (refreshed) {
				clientDebugLog('info', '[OAuthDebug] access token refreshed', {
					minValidity,
					...getRefreshTokenDebugMeta(keycloak.refreshToken),
				});
				tokenRefreshCallbacks?.onTokenUpdated(keycloak.token);
			}

			scheduleKeycloakTokenRefresh(keycloak.token, minValidity);

			return keycloak.token;
		}
	} catch (error) {
		if (sessionExpiredHandled || logoutInProgress) {
			return null;
		}

		const fatal = await isKeycloakRefreshFatalError(error);
		if (fatal || !keycloak.authenticated) {
			const oauthError = await parseKeycloakOAuthError(error);
			clientDebugLog('warn', '[auth:refresh] fatal refresh failure', {
				fatal,
				authenticated: Boolean(keycloak.authenticated),
				minValidity,
				error: oauthError?.error,
				errorDescription: oauthError?.error_description,
				message: error instanceof Error ? error.message : String(error),
				...getRefreshTokenDebugMeta(keycloak.refreshToken),
			});
			handleKeycloakSessionExpired();

			return null;
		}

		clientDebugLog('warn', '[auth:refresh] non-fatal refresh failure', {
			authenticated: Boolean(keycloak.authenticated),
			minValidity,
			hasUsableAccessToken: Boolean(
				keycloak.token && isAccessTokenUsable(keycloak.token, minValidity),
			),
			message: error instanceof Error ? error.message : String(error),
			...getRefreshTokenDebugMeta(keycloak.refreshToken),
		});

		if (keycloak.token && isAccessTokenUsable(keycloak.token, minValidity)) {
			scheduleKeycloakTokenRefresh(keycloak.token, minValidity);

			return keycloak.token;
		}
	}

	if (sessionExpiredHandled || logoutInProgress) {
		return null;
	}

	if (!keycloak.authenticated) {
		handleKeycloakSessionExpired();
	}

	return null;
}

/**
 * Refreshes the access token when needed and returns the current token,
 * or `null` when refresh fails and no token is available.
 * Concurrent callers share a single in-flight refresh (API requests must await this).
 */
export async function ensureFreshKeycloakToken(
	minValidity = KEYCLOAK_MIN_TOKEN_VALIDITY_SECONDS
): Promise<string | null> {
	if (sessionExpiredHandled || logoutInProgress) {
		return null;
	}

	if (refreshInFlight) {
		return refreshInFlight;
	}

	refreshInFlight = runKeycloakTokenRefresh(minValidity).finally(() => {
		refreshInFlight = null;
	});

	return refreshInFlight;
}

function normalizeTokenRefreshCallbacks(
	callbacks: KeycloakTokenRefreshCallbacks | ((token: string) => void)
): KeycloakTokenRefreshCallbacks {
	return typeof callbacks === 'function' ? { onTokenUpdated: callbacks } : callbacks;
}

async function refreshOnAppActive(): Promise<void> {
	if (sessionExpiredHandled || logoutInProgress) {
		return;
	}

	const keycloak = getKeycloak();
	// Mid OTP / logged-out: visibility/resume listeners survive logout. Do not treat
	// "no tokens" as session expiry or the hub will bounce login-otp → login.
	if (!keycloak.authenticated && !keycloak.token && !keycloak.refreshToken) {
		return;
	}

	const token = await ensureFreshKeycloakToken(KEYCLOAK_MIN_TOKEN_VALIDITY_SECONDS);
	if (sessionExpiredHandled || logoutInProgress) {
		return;
	}

	if (token) {
		tokenRefreshCallbacks?.onTokenUpdated(token);
		return;
	}

	if (!getKeycloak().authenticated) {
		handleKeycloakSessionExpired();
	}
}

function attachVisibilityAndResumeListeners(): void {
	if (typeof window === 'undefined') {
		return;
	}

	if (!visibilityListenerAttached) {
		visibilityListenerAttached = true;
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') {
				void refreshOnAppActive();
			}
		});
		window.addEventListener('focus', () => {
			void refreshOnAppActive();
		});
	}

	if (Capacitor.isNativePlatform() && !nativeResumeListenerAttached) {
		nativeResumeListenerAttached = true;
		void App.addListener('appStateChange', ({ isActive }) => {
			if (isActive) {
				void refreshOnAppActive();
			}
		}).then((handle) => {
			removeNativeResumeListener = () => {
				void handle.remove();
			};
		});
	}
}

/** Registers Keycloak lifecycle hooks that refresh tokens and handle session expiry. */
export function bindKeycloakTokenLifecycle(
	callbacks: KeycloakTokenRefreshCallbacks | ((token: string) => void)
): void {
	if (typeof window === 'undefined') {
		return;
	}

	tokenRefreshCallbacks = normalizeTokenRefreshCallbacks(callbacks);
	const keycloak = getKeycloak();

	keycloak.onAuthRefreshError = () => {
		if (!keycloak.authenticated) {
			handleKeycloakSessionExpired();
		}
	};

	keycloak.onTokenExpired = () => {
		void (async () => {
			const token = await ensureFreshKeycloakToken(KEYCLOAK_MIN_TOKEN_VALIDITY_SECONDS);
			if (sessionExpiredHandled || logoutInProgress) {
				return;
			}

			if (token) {
				// ensureFresh notifies onTokenUpdated only when the token actually changed;
				// expiry always needs a client sync in case refresh was a no-op edge case.
				tokenRefreshCallbacks?.onTokenUpdated(token);
				return;
			}

			if (!getKeycloak().authenticated) {
				handleKeycloakSessionExpired();
			}
		})();
	};
}

/**
 * Starts proactive token refresh (scheduled at exp − 1 minute) plus resume/focus refresh,
 * and binds the token-expired lifecycle hook.
 */
export function startKeycloakTokenRefresh(
	callbacks: KeycloakTokenRefreshCallbacks | ((token: string) => void)
): void {
	sessionExpiredHandled = false;
	tokenRefreshCallbacks = normalizeTokenRefreshCallbacks(callbacks);
	stopKeycloakTokenRefresh();
	bindKeycloakTokenLifecycle(tokenRefreshCallbacks);
	attachVisibilityAndResumeListeners();

	const currentToken = getKeycloak().token;
	if (currentToken) {
		scheduleKeycloakTokenRefresh(currentToken);
	}
}

/** Stops the scheduled proactive refresh started by {@link startKeycloakTokenRefresh}. */
export function stopKeycloakTokenRefresh(): void {
	clearScheduledRefresh();
}

/** Stops background refresh and clears persisted Keycloak session storage. */
export function clearKeycloakSessionStorage(): void {
	sessionExpiredHandled = false;
	stopKeycloakTokenRefresh();
	clearPersistedKeycloakSession();
}

/** @internal Resets module state between unit tests. */
export function resetKeycloakTokenRefreshForTests(): void {
	sessionExpiredHandled = false;
	logoutInProgress = false;
	tokenRefreshCallbacks = undefined;
	refreshInFlight = null;
	scheduledForExp = undefined;
	visibilityListenerAttached = false;
	nativeResumeListenerAttached = false;
	removeNativeResumeListener?.();
	removeNativeResumeListener = undefined;
	stopKeycloakTokenRefresh();
}

function decodeJwtPayloadRecord(token: string): Keycloak.KeycloakTokenParsed | undefined {
	const parts = token.split('.');
	if (parts.length < 2) {
		return undefined;
	}

	try {
		const payload = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
		const parsed = JSON.parse(payload) as unknown;

		return typeof parsed === 'object' && parsed !== null
			? (parsed as Keycloak.KeycloakTokenParsed)
			: undefined;
	} catch {
		return undefined;
	}
}
